import React, { useState, useEffect, useCallback } from 'react';
import { generateDeviceFingerprint } from '../utils/DeviceFingerprintGenerator';

const DeviceIdentifier = () => {
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const detectBrowser = useCallback(() => {
    const ua = navigator.userAgent;

    if (ua.includes('Chrome') && !ua.includes('Edg')) {
      return 'Chrome';
    } else if (ua.includes('Firefox')) {
      return 'Firefox';
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      return 'Safari';
    } else if (ua.includes('Edg')) {
      return 'Edge';
    } else if (ua.includes('Opera') || ua.includes('OPR')) {
      return 'Opera';
    }

    return 'Unknown';
  }, []);

  const getOperatingSystem = useCallback(() => {
    const platform = navigator.platform;
    const userAgent = navigator.userAgent;

    if (platform.includes('Win')) {
      return 'Windows';
    } else if (platform.includes('Mac')) {
      return 'macOS';
    } else if (platform.includes('Linux')) {
      return 'Linux';
    } else if (/Android/i.test(userAgent)) {
      return 'Android';
    } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
      return 'iOS';
    }

    return 'Unknown';
  }, []);

  const getDeviceInfo = useCallback(() => {
    const screenObj = window.screen;
    return {
      screenWidth: screenObj.width,
      screenHeight: screenObj.height,
      availWidth: screenObj.availWidth,
      availHeight: screenObj.availHeight,
      colorDepth: screenObj.colorDepth,
      pixelDepth: screenObj.pixelDepth,
      devicePixelRatio: window.devicePixelRatio || 1,
      orientation: screenObj.orientation?.type || 'unknown'
    };
  }, []);

  const getWebGLInfo = useCallback(() => {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

      if (!gl) return null;

      return {
        vendor: gl.getParameter(gl.VENDOR),
        renderer: gl.getParameter(gl.RENDERER),
        version: gl.getParameter(gl.VERSION)
      };
    } catch (error) {
      return null;
    }
  }, []);

  const isMobileDevice = useCallback(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }, []);

  const collectDeviceInfo = useCallback(() => {
    const browser = detectBrowser();
    const os = getOperatingSystem();
    const device = getDeviceInfo();
    const webgl = getWebGLInfo();
    const isMobile = isMobileDevice();
    const fingerprint = generateDeviceFingerprint();

    return {
      browser: {
        name: browser,
        userAgent: navigator.userAgent,
        vendor: navigator.vendor,
        language: navigator.language,
        languages: navigator.languages,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine
      },
      operatingSystem: os,
      device: {
        ...device,
        isMobile,
        hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
        maxTouchPoints: navigator.maxTouchPoints || 0,
        connection: navigator.connection ? {
          effectiveType: navigator.connection.effectiveType,
          downlink: navigator.connection.downlink,
          rtt: navigator.connection.rtt
        } : null
      },
      webgl,
      fingerprint,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString()
    };
  }, [detectBrowser, getOperatingSystem, getDeviceInfo, getWebGLInfo, isMobileDevice]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const info = collectDeviceInfo();
      setDeviceInfo(info);
      setLoading(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [collectDeviceInfo]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(deviceInfo, null, 2))
        .then(() => alert('Informacje skopiowane do schowka!'))
        .catch(err => console.error('Błąd kopiowania:', err));
  };

  const copyFingerprintOnly = () => {
    navigator.clipboard.writeText(deviceInfo.fingerprint)
        .then(() => alert('Fingerprint skopiowany do schowka!'))
        .catch(err => console.error('Błąd kopiowania:', err));
  };

  if (loading) {
    return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Zbieranie informacji o urządzeniu...</p>
        </div>
    );
  }

  return (
      <div style={{
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        <h2>Identyfikator Urządzenia</h2>

        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
          <button
              onClick={copyToClipboard}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
          >
            Skopiuj wszystkie dane
          </button>
          <button
              onClick={copyFingerprintOnly}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
          >
            Skopiuj tylko fingerprint
          </button>
        </div>

        <div style={{ display: 'grid', gap: '20px' }}>
          <div style={{
            border: '2px solid #28a745',
            borderRadius: '8px',
            padding: '15px',
          }}>
            <h3>🔒 Unikalny Identyfikator</h3>
            <p><strong>Fingerprint:</strong> <code style={{
              padding: '4px 8px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '16px'
            }}>{deviceInfo.fingerprint}</code></p>
            <p><strong>Strefa czasowa:</strong> {deviceInfo.timezone}</p>
            <p><strong>Czas generowania:</strong> {new Date(deviceInfo.timestamp).toLocaleString('pl-PL')}</p>
          </div>

          <div style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '15px'
          }}>
            <h3>🌐 Przeglądarka</h3>
            <p><strong>Nazwa:</strong> {deviceInfo.browser.name}</p>
            <p><strong>Język:</strong> {deviceInfo.browser.language}</p>
            <p><strong>Obsługa cookies:</strong> {deviceInfo.browser.cookieEnabled ? 'Tak' : 'Nie'}</p>
            <p><strong>Status online:</strong> {deviceInfo.browser.onLine ? 'Online' : 'Offline'}</p>
          </div>

          <div style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '15px'
          }}>
            <h3>💻 System Operacyjny</h3>
            <p><strong>System:</strong> {deviceInfo.operatingSystem}</p>
            <p><strong>Platforma:</strong> {navigator.platform}</p>
          </div>

          <div style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '15px'
          }}>
            <h3>📱 Urządzenie</h3>
            <p><strong>Typ:</strong> {deviceInfo.device.isMobile ? 'Mobilne' : 'Desktop'}</p>
            <p><strong>Rozdzielczość ekranu:</strong> {deviceInfo.device.screenWidth} x {deviceInfo.device.screenHeight}</p>
            <p><strong>Głębia kolorów:</strong> {deviceInfo.device.colorDepth} bit</p>
            <p><strong>Pixel Ratio:</strong> {deviceInfo.device.devicePixelRatio}</p>
            <p><strong>Orientacja:</strong> {deviceInfo.device.orientation}</p>
            <p><strong>Rdzenie procesora:</strong> {deviceInfo.device.hardwareConcurrency}</p>
            <p><strong>Punkty dotyku:</strong> {deviceInfo.device.maxTouchPoints}</p>
          </div>

          {deviceInfo.webgl && (
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px'
              }}>
                <h3>🎮 WebGL</h3>
                <p><strong>Vendor:</strong> {deviceInfo.webgl.vendor}</p>
                <p><strong>Renderer:</strong> {deviceInfo.webgl.renderer}</p>
                <p><strong>Wersja:</strong> {deviceInfo.webgl.version}</p>
              </div>
          )}

          {deviceInfo.device.connection && (
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px'
              }}>
                <h3>📡 Połączenie</h3>
                <p><strong>Typ:</strong> {deviceInfo.device.connection.effectiveType}</p>
                <p><strong>Prędkość:</strong> {deviceInfo.device.connection.downlink} Mbps</p>
                <p><strong>RTT:</strong> {deviceInfo.device.connection.rtt} ms</p>
              </div>
          )}
        </div>

        <details style={{ marginTop: '20px' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
            📋 Szczegółowe informacje techniczne
          </summary>
          <pre style={{
            backgroundColor: '#f1f1f1',
            padding: '15px',
            borderRadius: '5px',
            overflow: 'auto',
            fontSize: '12px'
          }}>
            {JSON.stringify(deviceInfo, null, 2)}
          </pre>
        </details>
      </div>
  );
};

export default DeviceIdentifier;